import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface LightboxImage {
  src: string;
  title: string;
  caption: string;
}
interface ImageLightboxProps {
  images: LightboxImage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
}
export function ImageLightbox({
  images,
  index,
  onClose,
  onChange
}: ImageLightboxProps) {
  const isOpen = index !== null;
  const prev = () => index !== null && onChange((index - 1 + images.length) % images.length);
  const next = () => index !== null && onChange((index + 1) % images.length);
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index]);
  return <AnimatePresence data-id="element-146" data-mp="1kdvnpp0w1qmf">
      {isOpen && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={images[index].title} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="fixed inset-0 z-[100] bg-black/95 flex flex-col" onClick={onClose} data-id="element-147" data-mp="1kfged3k30pf0">
          <div className="flex items-center justify-between px-6 py-4 text-white" data-id="element-148" data-mp="233lem11j5s92">
            <span className="text-sm text-gray-400 tabular-nums" data-id="element-149" data-mp="2825060c7p20w">
              {index + 1} / {images.length}
            </span>
            <button onClick={onClose} className="p-2 hover:text-[#C5A55A] transition-colors" aria-label="Close gallery" data-id="element-150" data-mp="rnn3zsqpey5q">
              <X className="w-6 h-6" data-id="element-151" data-mp="ckn1sxupy391" />
            </button>
          </div>

          <div className="relative flex-1 flex items-center justify-center px-4 md:px-20 min-h-0" data-id="element-152" data-mp="8k31jleumjio">
            <motion.img key={images[index].src} src={images[index].src} alt={images[index].title} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-full max-w-full object-contain" onClick={e => e.stopPropagation()} data-id="element-153" data-mp="3stdgp8y7o8w" />

            <button onClick={e => {
          e.stopPropagation();
          prev();
        }} className="absolute left-2 md:left-6 p-3 text-white bg-black/50 hover:bg-[#C5A55A] hover:text-black rounded-full transition-colors" aria-label="Previous photo" data-id="element-154" data-mp="3h0eo1oln9nku">
              <ChevronLeft className="w-6 h-6" data-id="element-155" data-mp="1ht7rrkj8jubo" />
            </button>
            <button onClick={e => {
          e.stopPropagation();
          next();
        }} className="absolute right-2 md:right-6 p-3 text-white bg-black/50 hover:bg-[#C5A55A] hover:text-black rounded-full transition-colors" aria-label="Next photo" data-id="element-156" data-mp="1hsy07sijlf3v">
              <ChevronRight className="w-6 h-6" data-id="element-157" data-mp="yubycbf7bjyw" />
            </button>
          </div>

          <div className="px-6 py-6 text-center" onClick={e => e.stopPropagation()} data-id="element-158" data-mp="ywoo3ar7aymi">
            <p className="text-white font-serif text-xl" data-id="element-159" data-mp="18srq8gm1yx9g">
              {images[index].title}
            </p>
            <p className="text-gray-400 text-sm mt-1" data-id="element-160" data-mp="1cud86gdg21qi">
              {images[index].caption}
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}